/**
 * Essai Solo — état et démarrage.
 *
 * Point d'entrée unique côté client. Tout écran qui parle de l'essai passe par
 * ici, pour ne pas reproduire ce qui est arrivé aux groupes : trois
 * dérivations indépendantes et incohérentes du même état.
 *
 * Ce hook n'AUTORISE rien. La vérité est en base : `start_solo_trial` porte
 * six gardes, et `is_paid_plan_for_product` décide de l'accès. Ici, on
 * affiche, et on déclenche.
 */
import { useCallback, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import { UsageAnalyticsTracker } from "../services/UsageAnalyticsTracker";
import {
  parseTrialRefusal,
  parseTrialOfferRefusal,
  trialSnoozeUntil,
  trialOfferPending,
  trialSnoozeElapsed,
  type TrialRefusal,
  type TrialOfferRefusal,
  type TrialSnoozeChoice,
  canStartTrial,
  hasUsedTrial,
  isTrialActive,
  trialDaysRemaining,
  trialEndsAt,
} from "../constants/quotas";

export interface StartTrialOptions {
  /**
   * Consentement explicite aux e-mails de prospection (relance J+7).
   * Défaut `false` : une case non cochée ne consent à rien, et un appelant
   * qui oublie l'option ne doit pas consentir à la place de l'utilisateur.
   */
  marketingOptIn?: boolean;
}

export interface SoloTrialState {
  /** Essai en cours. */
  isActive: boolean;
  /** Jours restants, arrondis vers le haut. `null` hors essai. */
  daysRemaining: number | null;
  /** Date de fin, pour l'afficher telle quelle. `null` hors essai. */
  endsAt: Date | null;
  /** Un essai a déjà été consommé — en cours ou terminé. */
  hasUsed: boolean;
  /** Compte Gratuit n'ayant jamais essayé : la cible de l'offre. */
  canStart: boolean;
  /** Appel en cours. */
  starting: boolean;
  /** Dernier refus reçu, à traduire côté écran. */
  refusal: TrialRefusal | null;
  /** La proposition doit-elle être affichée ? */
  offerPending: boolean;
  /** Le report est arrivé à échéance : autre texte, pas le message initial. */
  snoozeElapsed: boolean;
  /** Appel de réponse en cours (refus ou report). */
  responding: boolean;
  /** Dernier refus reçu sur une réponse à l'offre. */
  offerRefusal: TrialOfferRefusal | null;
  /**
   * « Non merci ». Le serveur l'enregistre pour la campagne en cours : ce
   * n'est pas un refus définitif, c'est un refus de CETTE proposition.
   */
  declineOffer: () => Promise<boolean>;
  /**
   * « Plus tard », avec l'échéance que la personne a estimée et, séparément,
   * son consentement à être prévenue. Refuser le rappel n'annule pas le
   * report : deux réponses distinctes.
   */
  snoozeOffer: (
    choice: TrialSnoozeChoice,
    remind?: boolean,
  ) => Promise<boolean>;
  /**
   * Démarre l'essai. Résout à `true` si l'essai vient d'être accordé.
   * Rafraîchit le profil au passage — sans quoi l'interface continuerait
   * d'afficher l'état d'avant jusqu'au prochain montage.
   *
   * `marketingOptIn` porte la case NON pré-cochée de l'écran de démarrage
   * (décision 4). Elle n'est vraie que si la personne l'a cochée elle-même.
   */
  startTrial: (options?: StartTrialOptions) => Promise<boolean>;
}

export function useSoloTrial(): SoloTrialState {
  const { user, profile, refreshProfile } = useAuth();
  const [starting, setStarting] = useState(false);
  const [refusal, setRefusal] = useState<TrialRefusal | null>(null);
  const [responding, setResponding] = useState(false);
  const [offerRefusal, setOfferRefusal] = useState<TrialOfferRefusal | null>(
    null,
  );

  const startTrial = useCallback(
    async ({
      marketingOptIn = false,
    }: StartTrialOptions = {}): Promise<boolean> => {
      setStarting(true);
      setRefusal(null);
      try {
        // Le produit est passé EXPLICITEMENT. AuthContext vit dans packages/core
        // et est monté par quatre applications : sans ce paramètre, un compte
        // créé sur DooQuest consommerait son essai DooDates. La RPC refuse tout
        // produit autre que « date » — la garde est serveur, celle-ci n'est que
        // la contrepartie honnête côté client.
        const { error } = await supabase.rpc("start_solo_trial", {
          p_product: "date",
        });

        if (error) {
          const code = parseTrialRefusal(error.message);
          if (code) {
            // Refus attendu : pas de logger.error, l'écran expliquera.
            setRefusal(code);
            logger.debug("Solo trial refused", "quota", { code });
            return false;
          }
          logger.error("Failed to start Solo trial", "quota", error);
          return false;
        }

        // Le consentement est écrit APRÈS, et séparément. L'essai est ce que la
        // personne a demandé ; le consentement est un supplément. Dans cet ordre,
        // un échec d'écriture du consentement coûte un e-mail de relance non
        // envoyé — jamais un essai non accordé. L'inverse serait impardonnable.
        if (marketingOptIn && user) {
          const { error: consentError } = await supabase
            .from("profiles")
            .update({ marketing_emails_opt_in_at: new Date().toISOString() })
            .eq("id", user.id);
          if (consentError) {
            logger.warn(
              "Solo trial started but marketing consent was not recorded",
              "quota",
              consentError,
            );
          }
        }

        await refreshProfile();
        void UsageAnalyticsTracker.track("trial_started", {});
        return true;
      } finally {
        setStarting(false);
      }
    },
    [refreshProfile, user],
  );

  /**
   * Un seul chemin pour les deux réponses : elles ne diffèrent que par les
   * arguments. En écrire deux donnerait deux endroits où oublier le
   * rafraîchissement du profil, et le bandeau resterait affiché après un
   * « non merci » — soit précisément ce que la personne vient de refuser.
   */
  const repondre = useCallback(
    async (
      response: "declined" | "snoozed",
      snoozeUntil: string | null,
      remind: boolean,
    ): Promise<boolean> => {
      setResponding(true);
      setOfferRefusal(null);
      try {
        const { error } = await supabase.rpc("respond_to_trial_offer", {
          p_response: response,
          p_snooze_until: snoozeUntil,
          p_remind: remind,
          // Explicite, comme pour start_solo_trial : AuthContext est monté par
          // quatre applications, et l'offre n'existe que pour « date ».
          p_product: "date",
        });
        if (error) {
          const code = parseTrialOfferRefusal(error.message);
          if (code) {
            setOfferRefusal(code);
            logger.debug("Trial offer response refused", "quota", { code });
            return false;
          }
          logger.error("Failed to respond to trial offer", "quota", error);
          return false;
        }
        await refreshProfile();
        return true;
      } finally {
        setResponding(false);
      }
    },
    [refreshProfile],
  );

  const declineOffer = useCallback(
    () => repondre("declined", null, false),
    [repondre],
  );

  const snoozeOffer = useCallback(
    (choice: TrialSnoozeChoice, remind = false) =>
      repondre("snoozed", trialSnoozeUntil(choice), remind),
    [repondre],
  );

  return {
    offerPending: trialOfferPending(profile),
    snoozeElapsed: trialSnoozeElapsed(profile),
    responding,
    offerRefusal,
    declineOffer,
    snoozeOffer,
    isActive: isTrialActive(profile),
    daysRemaining: trialDaysRemaining(profile),
    endsAt: trialEndsAt(profile),
    hasUsed: hasUsedTrial(profile),
    canStart: canStartTrial(profile),
    starting,
    refusal,
    startTrial,
  };
}
